import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Dialog, DialogContent, DialogTitle, Switch, TextField, Typography } from "@mui/material";
import toast from "react-hot-toast";
import { api } from "../../api/api";
import { tr } from "../../i18n";

/** What the screen is doing now, as Notiz Present reports it. */
const STATE_TEXT = {
  off: "Off",
  waiting: "On: enter the code shown on the screen to connect it",
  ready: "On: the code shows on the screen",
  connecting: "On: someone is connecting",
  presenting: "On: someone is sharing their screen",
  offline: "On, but the screen is not connected",
};

const REFRESH_MS = 15000;

function stateText(present) {
  if (!present.enabled) return STATE_TEXT.off;
  if (!present.paired) return STATE_TEXT.waiting;
  return STATE_TEXT[present.state] || STATE_TEXT.offline;
}

/**
 * "Dela skärm (Present)": switch screen sharing on or off for one screen. The first time, the screen shows a
 * pairing code ("Koppla skärmen"), which is entered here, the same way a screen is added. Hidden when screen
 * sharing is not set up on this server.
 */
export default function PresentCard({ screenId, canEdit }) {
  const [present, setPresent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [askCode, setAskCode] = useState(false);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [link, setLink] = useState(null);
  const [askNewLink, setAskNewLink] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/screen/present/${screenId}`);
      setPresent(data);
    } catch (error) {
      if (error?.response?.status === 503 || error?.response?.status === 404) setPresent(undefined);
    }
  }, [screenId]);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  if (!present) return null;

  const toggle = async (enabled) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/screen/present/${screenId}`, { enabled });
      setPresent(data);
      if (enabled && !data.paired) {
        setCode("");
        setCodeError("");
        setAskCode(true);
      } else {
        toast.success(enabled ? tr("Screen sharing switched on.") : tr("Screen sharing switched off."));
      }
    } catch (error) {
      toast.error(tr("Could not change screen sharing. Try again shortly."));
    } finally {
      setBusy(false);
    }
  };

  const pair = async () => {
    setBusy(true);
    setCodeError("");
    try {
      const { data } = await api.post(`/screen/present/${screenId}/pair`, { code });
      setPresent(data);
      setAskCode(false);
      toast.success(tr("The screen is connected."));
    } catch (error) {
      const status = error?.response?.status;
      setCodeError(
        status === 400
          ? tr("Wrong code, or the code has expired. Check the screen and try again.")
          : status === 429
          ? tr("Too many tries. Wait a minute and try again.")
          : tr("Could not connect the screen. Try again shortly.")
      );
    } finally {
      setBusy(false);
    }
  };

  const newLink = async () => {
    setAskNewLink(false);
    setBusy(true);
    try {
      const { data } = await api.post(`/screen/present/${screenId}/link`);
      setLink(data.setupLink);
    } catch (error) {
      toast.error(tr("Could not make a new setup link. Try again shortly."));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success(tr("Copied."));
    } catch (error) {
      toast.error(tr("Could not copy. Select the link and copy it by hand."));
    }
  };

  const linkButton = { mt: 1, px: 0, mr: 2, color: "#008080", textTransform: "none" };

  return (
    <Box sx={{ background: "white", p: 2, margin: "18px 20px", borderRadius: "16px" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Box>
          <Typography sx={{ color: "#15192C", fontSize: "16px", fontWeight: "bold" }}>{tr("Screen sharing (Present)")}</Typography>
          <Typography sx={{ color: "#92959E", fontSize: "14px" }}>{tr(stateText(present))}</Typography>
        </Box>
        <Switch
          checked={present.enabled}
          disabled={!canEdit || busy}
          onChange={(e) => toggle(e.target.checked)}
          inputProps={{ "aria-label": tr("Screen sharing (Present)") }}
          sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#008080" }, "& .Mui-checked + .MuiSwitch-track": { backgroundColor: "#008080" } }}
        />
      </Box>
      <Typography sx={{ color: "#92959E", fontSize: "13px", mt: 1 }}>
        {tr("Lets people show their computer screen on this screen with a code. Keep it off for lobby or menu screens.")}
      </Typography>
      {canEdit && present.enabled && (
        <Box>
          <Button variant="text" disabled={busy} onClick={() => { setCode(""); setCodeError(""); setAskCode(true); }} sx={linkButton}>
            {present.paired ? tr("Connect the screen again") : tr("Enter the code from the screen")}
          </Button>
          <Button variant="text" disabled={busy} onClick={() => setAskNewLink(true)} sx={linkButton}>
            {tr("Setup link for the screen app")}
          </Button>
        </Box>
      )}

      <Dialog open={askCode} onClose={() => setAskCode(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{tr("Connect the screen")}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "14px", mb: 2 }}>
            {tr("Within a minute, the screen shows \"Koppla skärmen\" with an 8-digit code in a corner. Enter it here.")}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^0-9 ]/g, "").slice(0, 9))}
            onKeyDown={(e) => { if (e.key === "Enter" && code.replace(/\s/g, "").length === 8) pair(); }}
            placeholder="0000 0000"
            inputProps={{ inputMode: "numeric", "aria-label": tr("Code from the screen") }}
            error={Boolean(codeError)}
            helperText={codeError || " "}
          />
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1 }}>
            <Button onClick={() => setAskCode(false)} sx={{ textTransform: "none" }}>{tr("Later")}</Button>
            <Button
              variant="contained"
              disabled={busy || code.replace(/\s/g, "").length !== 8}
              onClick={pair}
              sx={{ textTransform: "none", background: "#008080" }}
            >
              {tr("Connect")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={askNewLink} onClose={() => setAskNewLink(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{tr("Setup link for the screen app?")}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "14px", mb: 2 }}>
            {tr("Only for screens with the Present screen app that cannot show a code. The screen's current connection stops working at once.")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button onClick={() => setAskNewLink(false)} sx={{ textTransform: "none" }}>{tr("Cancel")}</Button>
            <Button variant="contained" onClick={newLink} sx={{ textTransform: "none", background: "#008080" }}>
              {tr("Make a new link")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(link)} onClose={() => setLink(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{tr("Setup link for the screen")}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "14px", mb: 2 }}>
            {tr("Open this link once in the screen app on the panel. It works like a password: do not share it or save it anywhere shared. It is only shown now.")}
          </Typography>
          <TextField value={link || ""} fullWidth InputProps={{ readOnly: true }} onFocus={(e) => e.target.select()} size="small" />
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 2 }}>
            <Button onClick={copy} sx={{ textTransform: "none", color: "#008080" }}>{tr("Copy")}</Button>
            <Button variant="contained" onClick={() => setLink(null)} sx={{ textTransform: "none", background: "#008080" }}>
              {tr("Done")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
